import { useState } from "react";
import PdfModal from "./PdfModal";

/* ===================================================== */
/* ================= ROOT RENDERER ===================== */
/* ===================================================== */

export default function ResponseRenderer({ data }) {
    if (!data) return null;

    if (data.success === false) {
        return <FallbackCard data={data.message} />;
    }

    // 🔥 NEW: success true but no data → show message cleanly
    if (data.success === true && !data.data && data.message) {
        return (
            <div className="max-w-5xl mx-auto pt-2 pb-2">
                <MessageCard message={data.message} />
            </div>
        );
    }

    if (data.success && data.data) {
        data = data.data;
    }

    if (Array.isArray(data)) {
        return (
            <div className="max-w-5xl mx-auto space-y-10 pt-2 pb-2">
                {data.map((item, i) => (
                    <SmartCard key={i} data={item} />
                ))}
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-10 pt-2 pb-2">
            <SmartCard data={data} />
        </div>
    );
}
/* ===================================================== */
/* ================= SMART ROUTER ====================== */
/* ===================================================== */

function SmartCard({ data }) {
    if (!data) return null;

    // 🔥 HANDLE SINGLE FIELD RESPONSE
    if (isSingleFieldObject(data)) {
        return <SingleFieldRenderer data={data} />;
    }

    if (data.trialNumber) return <TrialCard data={data} />;
    if (data.caseType === "REEXAM") return <ReexamCard data={data} />;

    return <GenericCard data={data} />;
}
function isSingleFieldObject(obj) {
    if (!obj || typeof obj !== "object") return false;

    const keys = Object.keys(obj).filter(k => k !== "_id");

    return keys.length === 1;
}
function formatLabel(label) {
    return label
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase());
}
function renderValue(value) {
    if (Array.isArray(value)) {
        return value.join(", ");
    }

    return value;
}
function SingleFieldRenderer({ data }) {
    const entries = Object.entries(data).filter(
        ([key]) => key !== "_id"
    );

    const [key, value] = entries[0];

    return (
        <div className="text-sm text-gray-800">
            <span className="font-semibold text-[#102D33] capitalize">
                {formatLabel(key)}:
            </span>{" "}
            <span>{renderValue(value)}</span>
        </div>
    );
}
/* ===================================================== */
/* ================= TRIAL CARD ======================== */
/* ===================================================== */

function TrialCard({ data }) {
    const [documents, setDocuments] = useState([]);
    const [showDocs, setShowDocs] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [loadingDocs, setLoadingDocs] = useState(false);

    const fetchDocuments = async () => {
        try {
            setLoadingDocs(true);

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/documents/${data.trialNumber}`
            );

            const result = await res.json();

            setDocuments(result.patentTrialDocumentDataBag || []);
            setShowDocs(true);
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            setLoadingDocs(false);
        }
    };

    return (
        <div className="space-y-8">

            <HeaderCard
                title={data.trialNumber}
                badge={data.trialType}
                subtitle={`Filed: ${data.petitionFilingDate}`}
            />

            <Section title="Patent Information">
                <Field label="Patent Number" value={data.patentNumber} />
                <Field label="Application Number" value={data.applicationNumber} />
                <Field label="Grant Date" value={data.grantDate} />
                <Field label="Invention Title" value={data.inventionTitle} />
            </Section>

            <Section title="Ownership">
                <Field label="Original Assignee" value={data.originalAssignee} />
                <Field label="Current Owner" value={data.currentPatentOwner} />
            </Section>

            {data.inventors?.length > 0 && (
                <Section title="Inventors">
                    <TagList items={data.inventors} />
                </Section>
            )}

            {data.petitioner && (
                <Section title="Petitioner">
                    <Field label="Name" value={data.petitioner} />

                    {data.petitionerCounsel?.length > 0 && (
                        <>
                            <div className="text-xs font-semibold mt-3 text-gray-600">
                                Counsel
                            </div>
                            <TagList items={data.petitionerCounsel} />
                        </>
                    )}

                    {data.petitionerEmails?.length > 0 && (
                        <>
                            <div className="text-xs font-semibold mt-3 text-gray-600">
                                Emails
                            </div>
                            <EmailList emails={data.petitionerEmails} />
                        </>
                    )}
                </Section>
            )}

            {data.assignments?.length > 0 && (
                <Section title="Assignments">
                    <Assignments assignments={data.assignments} />
                </Section>
            )}

            <button
                onClick={fetchDocuments}
                disabled={loadingDocs}
                className={`bg-gradient-to-r from-[#0f2f36] to-[#174a52] 
    text-white px-6 py-2 rounded-full text-xs font-medium shadow-md 
    flex items-center gap-2 transition-all duration-200
    ${loadingDocs ? "opacity-70 cursor-not-allowed" : "hover:scale-105"}`}
            >
                {loadingDocs ? (
                    <>
                        <Spinner />
                        Loading...
                    </>
                ) : (
                    "📄 View Documents"
                )}
            </button>

            {/* ================= DOCUMENT SIDEBAR ================= */}

            {showDocs && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                        onClick={() => setShowDocs(false)}
                    />

                    <div className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col animate-slideIn">

                        <div className="bg-gradient-to-r from-[#0f2f36] to-[#174a52] text-white px-5 py-4 flex justify-between items-center">
                            <div className="text-sm font-semibold">
                                Documents ({documents.length})
                            </div>
                            <button
                                onClick={() => setShowDocs(false)}
                                className="text-white text-lg"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50">
                            {documents.map((doc, i) => {
                                const d = doc.documentData;

                                return (
                                    <div
                                        key={i}
                                        onClick={() =>
                                            setSelectedDoc({
                                                trialNumber: data.trialNumber,
                                                documentIdentifier: d.documentIdentifier,
                                                documentTitle: d.documentName,
                                            })
                                        }
                                        className="bg-white rounded-xl p-4 cursor-pointer hover:shadow-lg hover:-translate-y-1 transform transition-all duration-200 border border-gray-100"
                                    >
                                        <div className="font-semibold text-[#102D33] truncate">
                                            {d.documentName}
                                        </div>
                                        <div className="text-gray-500 text-xs mt-1">
                                            {d.documentCategory}
                                        </div>
                                        <div className="text-gray-400 text-[10px] mt-1">
                                            Filed: {d.documentFilingDate}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {selectedDoc && (
                <PdfModal
                    documentData={selectedDoc}
                    onClose={() => setSelectedDoc(null)}
                />
            )}
        </div>
    );
}

function Spinner() {
    return (
        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
    );
}
/* ===================================================== */
/* ================= REEXAM CARD ======================= */
/* ===================================================== */

function ReexamCard({ data }) {
    const entityStatusCategory =
        data.entityStatusData?.businessEntityStatusCategory ||
        data.entityStatus;

    const smallEntity =
        data.entityStatusData?.smallEntityStatusIndicator;

    const statusText =
        data.applicationStatusDescriptionText ||
        data.status;

    const statusDate =
        data.applicationStatusDate ||
        data.statusDate;

    return (
        <div className="space-y-8">

            <HeaderCard
                title={`Reexamination - ${data.applicationNumber}`}
                badge="REEXAM"
                subtitle={statusText}
            />

            {/* ================= CASE INFO ================= */}
            <Section title="Case Information">
                <Field label="Parent Patent" value={data.parentPatentNumber} />
                <Field label="Application Type" value={data.applicationTypeCode} />
                <Field label="Filing Date" value={data.filingDate} />
                <Field label="Effective Filing Date" value={data.effectiveFilingDate} />
                <Field label="Status Date" value={statusDate} />
                <Field label="Art Unit" value={data.artUnit} />
                {data.examiner && (
                    <Field label="Examiner" value={data.examiner} />
                )}
                <Field label="Entity Status" value={entityStatusCategory} />
                {typeof smallEntity === "boolean" && (
                    <Field
                        label="Small Entity"
                        value={smallEntity ? "Yes" : "No"}
                    />
                )}
                <Field label="Title" value={data.title || data.inventionTitle} />
            </Section>

            {/* ================= PARTIES ================= */}
            {(data.patentOwner || data.requester) && (
                <Section title="Parties">
                    <Field label="Patent Owner" value={data.patentOwner} />
                    <Field label="Requester" value={data.requester} />
                </Section>
            )}

            {/* ================= INVENTORS ================= */}
            {data.inventors?.length > 0 && (
                <Section title="Associated Names">
                    <TagList items={data.inventors} />
                </Section>
            )}

            {/* ================= CORRESPONDENCE ================= */}
            {data.correspondenceAddressBag?.length > 0 && (
                <Section title="Correspondence Address">
                    {data.correspondenceAddressBag.map((addr, i) => (
                        <div
                            key={i}
                            className="bg-gray-50 border rounded-xl p-4 text-xs space-y-1"
                        >
                            <div className="font-semibold text-[#102D33]">
                                {addr.nameLineOneText}
                            </div>
                            <div>{addr.addressLineOneText}</div>
                            {addr.addressLineTwoText && (
                                <div>{addr.addressLineTwoText}</div>
                            )}
                            <div>
                                {addr.cityName}, {addr.geographicRegionCode}{" "}
                                {addr.postalCode}
                            </div>
                            <div>{addr.countryName}</div>
                        </div>
                    ))}
                </Section>
            )}

            {/* ================= TIMELINE ================= */}
            {data.keyEvents?.length > 0 && (
                <Section title="Timeline">
                    <Timeline events={data.keyEvents} />
                </Section>
            )}
        </div>
    );
}

/* ===================================================== */
/* ================= FALLBACK CARD ===================== */
/* ===================================================== */

function FallbackCard({ data }) {
    if (!data) return null;

    return (
        <div className="bg-yellow-50 border border-yellow-300 rounded-2xl p-6 space-y-4 text-sm shadow">
            <div className="font-semibold text-lg">
                {data.message}
            </div>

            {data.details && (
                <div className="text-gray-700 space-y-1">
                    <div><strong>Reason:</strong> {data.details.reason}</div>
                    <div><strong>Confidence:</strong> {data.details.confidence}</div>
                </div>
            )}

            {data.next && (
                <div>
                    <div className="font-semibold mb-1">Next Steps:</div>
                    <ul className="list-disc list-inside">
                        {Object.values(data.next).map((v, i) => (
                            <li key={i}>{v}</li>
                        ))}
                    </ul>
                </div>
            )}

            {data.suggestions?.length > 0 && (
                <SuggestionList title="Suggestions" items={data.suggestions} />
            )}

            {data.examples?.length > 0 && (
                <SuggestionList title="Example Questions" items={data.examples} />
            )}
        </div>
    );
}

/* ===================================================== */
/* ================= GENERIC CARD ====================== */
/* ===================================================== */

function GenericCard({ data }) {
    return (
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <pre className="whitespace-pre-wrap text-sm">
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );
}

function MessageCard({ message }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-sm shadow-sm">
            <div className="text-[#102D33] font-medium leading-relaxed">
                {message}
            </div>
        </div>
    );
}
/* ===================================================== */
/* ================= UI COMPONENTS ===================== */
/* ===================================================== */

function HeaderCard({ title, badge, subtitle }) {
    return (
        <div className="bg-gradient-to-br from-[#0f2f36] to-[#174a52] text-white rounded-2xl p-6 shadow-xl">
            <div className="text-xl font-semibold">{title}</div>
            <div className="flex gap-3 mt-3 text-xs items-center">
                {badge && (
                    <span className="bg-white/20 px-3 py-1 rounded-full">
                        {badge}
                    </span>
                )}
                {subtitle && <span className="text-gray-200">{subtitle}</span>}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
            <h3 className="font-semibold text-[#102D33] text-sm uppercase mb-4">
                {title}
            </h3>
            <div className="space-y-3 text-sm text-gray-700">
                {children}
            </div>
        </div>
    );
}

function Field({ label, value }) {
    if (!value) return null;

    return (
        <div className="flex flex-col border-b border-gray-100 pb-3">
            <span className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                {label}
            </span>
            <span className="font-medium text-gray-800 break-words whitespace-pre-wrap">
                {value}
            </span>
        </div>
    );
}

function TagList({ items }) {
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((item, i) => (
                <span
                    key={i}
                    className="bg-[#102D33]/10 text-[#102D33] px-3 py-1 rounded-full text-xs"
                >
                    {item}
                </span>
            ))}
        </div>
    );
}

function EmailList({ emails }) {
    return (
        <div className="space-y-1">
            {emails.map((email, i) => (
                <a
                    key={i}
                    href={`mailto:${email}`}
                    className="block text-blue-600 underline text-xs hover:text-blue-800"
                >
                    {email}
                </a>
            ))}
        </div>
    );
}

function Assignments({ assignments }) {
    const [open, setOpen] = useState(false);

    return (
        <div>
            <button
                onClick={() => setOpen(!open)}
                className="text-blue-600 text-xs underline mb-3"
            >
                {open ? "Hide Assignments" : "View Assignments"}
            </button>

            {open &&
                assignments.map((a, i) => (
                    <div
                        key={i}
                        className="bg-gray-50 border rounded-xl p-4 text-xs mb-3"
                    >
                        <div><strong>Assignee:</strong> {a.assignee}</div>
                        <div><strong>Assignor:</strong> {a.assignor}</div>
                        <div><strong>Record Date:</strong> {a.recordDate}</div>
                    </div>
                ))}
        </div>
    );
}

function Timeline({ events }) {
    return (
        <div className="space-y-4">
            {events.map((e, i) => {
                const description =
                    e.eventDescription || e.description;

                const date =
                    e.eventDate || e.date;

                return (
                    <div key={i} className="flex gap-3">
                        <div className="w-2 h-2 bg-[#102D33] rounded-full mt-2"></div>
                        <div>
                            <div className="font-medium">
                                {description}
                            </div>
                            <div className="text-xs text-gray-500">
                                {date} • {e.eventCode}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
function SuggestionList({ title, items }) {
    return (
        <div>
            <div className="font-semibold mb-1">{title}:</div>
            <ul className="list-disc list-inside">
                {items.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>
        </div>
    );
}